<template>
  <footer class="bg-gray-800 text-white flex-shrink-0 position-relative">
    <section class="footer-upper px-4 px-xl-5">
      <div :class="{'container': container}">
        <b-row class="justify-content-between align-items-center h-100">
          <b-col class="d-flex flex-lg-row flex-column align-items-lg-center">
            <div class="d-none d-lg-flex align-items-center flex-grow-1">
              <img :src="OTALogo"
                   alt="One Though Animal"
                   width="214"
                   class="py-2"
              >
            </div>
            <div v-if="languages && languages.length"
                 class="d-flex align-items-center justify-content-center"
            >
              <b-icon icon="globe-americas" :class="show ? 'text-black' : 'text-white'" class="globe-icon" />
              <b-form-select v-model="pickedLanguage"
                             :disabled="selectorDisabled"
                             :options="languages"
                             class="border-0"
                             @focus="show = true"
                             @blur="show = false"
                             @change="emit('languagePicked', pickedLanguage)"
              />
            </div>
            <div v-if="privacyPolicy" class="mb-2 pt-2 ps-4 text-center">
              <b-link @click="showPrivacyPolicyModal">{{ privacyPolicy }}</b-link>
            </div>
          </b-col>
        </b-row>
      </div>
    </section>
    <section class="disclaimer px-4 px-xl-5">
      <div class="py-4 pt-0 pt-xl-4 d-lg-flex" :class="{'container': container}" data-test-id="footer-disclaimer">
        <div class="small"
             data-test-id="footer-copy-text"
             v-html="disclaimer.replace('[current-year]', String(new Date().getFullYear()))"
        />
      </div>
    </section>
    <b-modal
      v-model="showModal"
      size="xl"
      hide-footer
    >
      <template #title>
        <span>{{ privacyPolicy }}</span>
      </template>

      <div class="m-4" v-html="privacyPolicyContent" />
    </b-modal>
  </footer>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

interface Props {
  container?: boolean
  disclaimer: string
  privacyPolicy?: string
  privacyPolicyContent?: string
  languages?: unknown[]
  selectedLanguage?: string
  selectorDisabled?: boolean
  otaLogoUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  container: false,
  selectedLanguage: '',
  selectorDisabled: false,
  otaLogoUrl: ''
})

const emit = defineEmits<{
  languagePicked: [language: string]
}>()

const OTALogo = computed(() => props.otaLogoUrl)
const show = ref(false)
const pickedLanguage = ref(props.selectedLanguage || '')
const showModal = ref(false)

watch(() => props.selectedLanguage, (v: string) => {
  pickedLanguage.value = v
})

function showPrivacyPolicyModal(): void {
  showModal.value = true
}
</script>

<style lang="scss" scoped>
@import '../scss/colors';
@import '../scss/grid';

footer {
  .footer-upper {
    min-height: 8.5rem;
    display: flex;
    align-items: center;

    > div {
      width: 100%;
    }
  }

  a {
    color: $white;

    :active {
      color: $secondary;
    }
  }

  .disclaimer {
    background-color: $dark;
    color: $white;

    @include media-breakpoint-up('lg') {
      background-color: $white;
      color: $black !important; // override text-white !important on footer
    }
  }
  .globe-icon {
    position: relative;
    left: 1.5rem;
    top: 0.075em;
  }
  .form-select {
    background-color: #333;
    background-image: linear-gradient(45deg, transparent 50%, #fff 50%),
                      linear-gradient(135deg, #fff 50%, transparent 50%);
    background-position: calc(100% - 1rem) center, calc(100% - 0.625rem) center;
    background-size: 0.375rem 0.375rem, 0.375rem 0.375rem;
    background-repeat: no-repeat;
    border: none;
    color: white;
    padding-left: 2rem;
    padding-right: 2rem;
    width: auto;
    min-width: 1rem;
    &:focus {
      background-color: $white;
      background-image: linear-gradient(45deg, #000 50%, transparent 50%),
                        linear-gradient(135deg, transparent 50%, #000 50%);
      background-position: calc(100% - 0.625rem) center, calc(100% - 1rem) center;
      background-size: 0.375rem 0.375rem, 0.375rem 0.375rem;
      background-repeat: no-repeat;
      box-shadow: none;
      color: black;
    }
  }
}
</style>
